import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API } from 'api';
import { useSelector } from 'react-redux';

import { DOMAINS, IMPORTANCE, SECTIONS } from '@repo/monitoring';

import { apiBasePath, apiName } from 'assets';
import { upsertAgreement } from 'helpers/agreements';
import { getMainAccountId } from 'store/account';
import { Agreement, AgreementKey } from 'types/Agreement';
import { getAgreementsQuery } from './useAgreements';

type NewAgreement = {
  tinyaccount_id: string;
  agreement_key: AgreementKey;
  kit_id: string;
};

// agreed_at is deliberately absent: the server stamps it
const createAgreement = async (
  newAgreement: NewAgreement,
): Promise<Agreement> => {
  const response = await API.put(
    apiName,
    `${apiBasePath}/agreements`,
    { body: newAgreement },
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.CONDITIONS,
        importance: IMPORTANCE.CRITICAL,
      },
      context: { ...newAgreement },
    },
  );

  return response.result;
};

const useCreateAgreement = () => {
  const queryClient = useQueryClient();
  const mainAccountId = useSelector(getMainAccountId);

  // errors are left to propagate rather than toasted here: the modal gates
  // access to the page, so a failed write has to keep it open
  return useMutation({
    mutationFn: createAgreement,
    onSuccess: agreement => {
      // written straight into the cache instead of invalidating, so the modal
      // closes on the response rather than after a second round-trip
      queryClient.setQueryData<Agreement[]>(
        getAgreementsQuery(mainAccountId).queryKey,
        previous => upsertAgreement(previous ?? [], agreement),
      );
    },
  });
};

export default useCreateAgreement;
